import { useEffect, useContext, useState } from 'react';
import type { CustomNode, CustomEdge } from '@src/components/custom-nodes/types';
import { buildLinkedListHashTable } from '@src/scenario-editor/utils/serializer';
import { validatorInstance, initInstanceWithDefinitions, type NodeDefinitionItem } from '@src/scenario-editor/validator/Validator';
import {
  applyArticles,
  applyFitFacts,
  applyGotoTargets,
  applyScenarios,
  applyTasks,
  validateNodes,
  type NodeLike,
} from '@src/scenario-editor/validator/headless';
import { getDndNodeItem } from '@src/atoms';
import { useAtom } from 'jotai';
import { FetchContext } from '@src/scenario-editor/providers/fetch.context';
import {
  historyPushAtom,
  nodeDefinitionsAtom,
  getHistoryNavigationFlag,
  type ValidationResultItem
} from '@src/atoms';
import { notifyConsumer, type CallbackFn } from '@src/scenario-editor/hooks/notify-consumer';

const PAYLOAD_KEY = 'scenario-editor';

const debounce = (fn: (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn, isInitialized?: boolean) => void, ms: number) => {
  let timeout: NodeJS.Timeout;
  return (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn, isInitialized?: boolean) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(nodes, edges, callback, isInitialized), ms);
  };
}

// The same check the server runs on a stored graph (validator/headless.ts).
const getValidationResults = (nodes: CustomNode[]) =>
  validateNodes(validatorInstance, nodes as unknown as NodeLike[]) as unknown as ValidationResultItem[];

const saveToLocalStorage = debounce((
    nodes: CustomNode[],
    edges: CustomEdge[],
    callback?: CallbackFn,
    isInitialized?: boolean
  ) => {
  const validationResults = getValidationResults(nodes);
  console.log('validationResults', validationResults);
  const payload = buildLinkedListHashTable(nodes, edges)
  localStorage.setItem(PAYLOAD_KEY, JSON.stringify(payload));

  if (!isInitialized) {
    return;
  }

  notifyConsumer(payload, validationResults as never, callback);
}, 500);

export const useSave = (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn) => {
  const [, pushHistoryRecord] = useAtom(historyPushAtom);
  const [nodeDefinitions] = useAtom(nodeDefinitionsAtom);
  const fetchContext = useContext(FetchContext)!;
  const [isInitialized, setIsInitialized] = useState(false);

  // ==================================================
  // INFO: this part should be moved to a separate hook

  useEffect(() => {
    initInstanceWithDefinitions(nodeDefinitions as Record<string, NodeDefinitionItem>);

    Promise.all([
      fetchContext.fetchFitFacts()
        .then((data) => {
          validatorInstance.setFetchContext('fit:facts', data as never);
          applyFitFacts(validatorInstance, {
            ids: data.data.ids.map(item => item.id),
            categories: data.data.categories.map(item => item.id),
            tags: data.data.tags.map(item => item.id),
          });
        })
        .catch((error) => {
          console.error('Error fetching fit facts', error);
        }),

      fetchContext.fetchArticles()
        .then((data) => {
          validatorInstance.setFetchContext('message:suggestions', data as never);
          applyArticles(validatorInstance, {
            ids: data.data.ids.map(item => item.id),
            categories: data.data.categories.map(item => item.id),
            tags: data.data.tags.map(item => item.id)
          });
        })
        .catch((error) => {
          console.log('Error fetching articles', error);
        }),

      fetchContext.fetchAllScenario()
        .then((data) => {
          validatorInstance.setFetchContext('scenario:picker', data as never);
          applyScenarios(validatorInstance, data.data.map(item => item.id));
        })
        .catch((error) => {
          console.error('Error fetching scenarios', error);
        })
    ]).then(() => {
      setIsInitialized(true);
    }).catch((error) => {
      console.error('Error during initialization:', error);
      setIsInitialized(true); // Set initialized even if some fetches fail
    });

    const tasksOptions =
      (nodeDefinitions?.['task:*']?.nodeInfoContent ?? [])
        .find(item => item.fieldName === 'nodeType')?.options ?? [];

    applyTasks(validatorInstance, tasksOptions.map(item => item.value));
    applyGotoTargets(validatorInstance, nodes as unknown as NodeLike[]);
  }, [nodeDefinitions, fetchContext, nodes.length]);
  // ==================================================

  // Re-fire saveToLocalStorage once init completes so any node changes
  // the user made while validators were still warming up are propagated
  // to the consumer callback. Without this, a saveToLocalStorage call
  // scheduled before init returns early and the next nodes-change is
  // required for the callback to ever fire - meaning the user's first
  // edit after opening a scenario can be silently dropped.
  useEffect(() => {
    if (!isInitialized) return;
    const arePlaceholdersRendered = nodes.some(node => node.type === 'PlaceholderNode');
    const dndNodeItem = getDndNodeItem();
    if (arePlaceholdersRendered || dndNodeItem) return;
    saveToLocalStorage(nodes, edges, callback, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  useEffect(() => {
    const historyNavigationFlag = getHistoryNavigationFlag();
    const arePlaceholdersRendered = nodes.some(node => node.type === 'PlaceholderNode');
    const dndNodeItem = getDndNodeItem();

    // INFO: do not save to local storage if placeholders are rendered or dnd node is in progress
    // move this to a separate logic at some point
    if (!arePlaceholdersRendered && !dndNodeItem) {
      saveToLocalStorage(nodes, edges, callback, isInitialized);

      const payload = buildLinkedListHashTable(nodes, edges);
      if (Object.keys(payload).length && !historyNavigationFlag) {
        console.log('pushing payload to history', payload);
        pushHistoryRecord(payload);
      }
    }
  }, [JSON.stringify(nodes)]);

  return localStorage.getItem(PAYLOAD_KEY);
};
